/* eslint-disable @typescript-eslint/indent */

/* Note: please be careful with indentation, we have the rule disabled because of
*  eslint not being able to properly indent js inside styled components
*/
// Core
import styled, { css } from 'styled-components';

// Libraries
import { DatePicker as AntDDatePicker } from 'antd';

// Config
import { BREAKPOINTS } from '../../../config/screen';

const commonTransition: string = 'transition: all 0.3s ease';

export const RangePicker = styled(AntDDatePicker.RangePicker)<{ width: number }>`
  width: 182px;
  border-radius: 4px;
  height: 32px;
  padding: 7px 8px;
  display: flex;
  align-items: center;
  min-width: 200px;
  background-color: ${({ theme }) => theme.color.datePicker.backgroundColor};
  ${commonTransition};

  -webkit-text-size-adjust: 100%;
  touch-action: manipulation;

  /************************* INPUT *************************/

  .ant-picker-range-separator + .ant-picker-input input {
    pointer-events: none;
  };

  :focus-within input,
  :focus-within input::placeholder,
  :focus-within .ant-picker-range-separator,
  .ant-picker-focused .ant-picker-input-active {
    color: ${({ theme }) => theme.color.datePicker.activeTextColor} !important;
  };

  &.ant-picker:hover, .ant-picker-focused {
    border-color: ${({ theme }) => theme.color.datePicker.hoverBorderColor};
  };

  &.ant-picker-focused {
    border-color: ${({ theme }) => theme.color.datePicker.hoverBorderColor};
    box-shadow: none;
  };

  /************************* ACTIVE BAR *************************/

  .ant-picker-active-bar {
    display: none;
  };

  /************************* SEPARATOR *************************/

  .ant-picker-range-separator {
    color: ${({ theme }) => theme.color.datePicker.separatorTextColor};
    font-size: 14px;
    padding: 0 4px;
  };

  /************************* HOVER *************************/

  :hover {
    ${commonTransition};
    border: 1px solid ${({ theme }) => theme.color.datePicker.hoverBorderColor} !important;
    cursor: pointer;

    input, input::placeholder, .ant-picker-range-separator {
      color: ${({ theme }) => theme.color.datePicker.hoverInputValueTextColor} !important;
      ${commonTransition};
    };
  };

  /************************* FOCUS *************************/

  :focus-within {
    border: 1px solid ${({ theme }) => theme.color.datePicker.focusBorderColor} !important;
  };

  :focus-within:hover {
    border: 1px solid ${({ theme }) => theme.color.datePicker.focusBorderColor} !important;
  };

  /************************* SUFFIX *************************/

  .ant-picker-suffix, .ant-picker-clear {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: unset;
    position: absolute;
    right: auto;
    background-color: ${({ theme }) => theme.color.datePicker.backgroundColor};

    svg {
      color: ${({ theme }) => theme.color.datePicker.suffixIconColor};
      ${commonTransition};
    };
  };

  .ant-picker-clear {
    svg {
      color: ${({ theme }) => theme.color.datePicker.clearIconColor};
      ${commonTransition};

      :hover {
        color: ${({ theme }) => theme.color.datePicker.hoverIconColor};
        ${commonTransition};
      };
    };

    @media (max-width: ${BREAKPOINTS.lg}px) and (orientation: landscape) {
      opacity: 1;
    };
  };

  /************************* INPUT DETAILS *************************/

  .ant-picker, .ant-picker-input > input {
    min-width: 56px;
    width: 100%;
    display: flex;
    color: ${({ theme }) => theme.color.datePicker.inputTextColor};
    -webkit-text-size-adjust: 100%;
    touch-action: manipulation;
  };

  input {
    font-family: ${({ theme }) => theme.font.regular} !important;
    -webkit-text-size-adjust: 100%;
    touch-action: manipulation;
    &::placeholder {
      font-family: ${({ theme }) => theme.font.regular} !important;
      font-size: 14px !important;
    };
  };

  .ant-picker-input:nth-child(1) {
    margin-left: 28px;
  };

  .ant-picker, .ant-picker-input {
    display: block;
    align-items: center;
    padding-top: 2px;
  };

  .ant-picker-input:nth-child(1) {
    width: ${({ width }) => `${width}px`};
  };

  .ant-picker-input:nth-child(3) {
    width: auto;
    max-width: 68px;
  };

  > div > input {
    font-size: 14px !important;
    font-family: ${({ theme }) => theme.font.regular} !important;
    -webkit-text-size-adjust: 100%;
    touch-action: manipulation;

    /************************* PLACEHOLDER *************************/

    ::placeholder {
      ${commonTransition};
      font-size: 14px;
    };

    /************************* HOVER *************************/

    :hover {
      cursor: pointer;
    };

    /************************* FOCUS *************************/

    :focus {
      ${commonTransition};

      ::placeholder {
        ${commonTransition};
      };
    };
  };
`;

export const DatePickerComponent = styled(AntDDatePicker)`
  width: 100%;
  height: 32px;
  padding: 12px;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.color.datePicker.backgroundColor};
  border-color: ${({ theme }) => theme.color.datePicker.borderColor};
  box-shadow: none !important;
  min-width: 240px;

  -webkit-text-size-adjust: 100%;
  touch-action: manipulation;

  /************************* FOCUS *************************/

  :focus-within {
    border: 1px solid ${({ theme }) => theme.color.datePicker.focusBorderColor};
  };

  /************************* SUFFIX *************************/

  .ant-picker-suffix, .ant-picker-clear > svg {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  };

  /************************* INPUT *************************/

  .ant-picker-input {
    display: flex;
    flex-direction: row-reverse;
    gap: 13px;
  };

  .ant-picker, .ant-picker-input > input {
    color: ${({ theme }) => theme.color.datePicker.inputTextColor};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 16px;
    height: 100%;
    -webkit-text-size-adjust: 100%;
    touch-action: manipulation;
    ::placeholder {
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 16px;
    };
  };

  /************************* SUFFIX DETAILS *************************/

  .ant-picker-suffix {
    margin-inline-start: unset;
  };

  .ant-picker-clear {
    width: max-content;
    left: 0;
  };

  .ant-picker-suffix, .ant-picker-clear {    
    svg {
      color: ${({ theme }) => theme.color.datePicker.suffixIconColor};
      ${commonTransition};
    };
    background-color: ${({ theme }) => theme.color.datePicker.backgroundColor};
  };

  .ant-picker-clear {
    svg {
      color: ${({ theme }) => theme.color.datePicker.clearIconColor};
      ${commonTransition};

      :hover {
        color: ${({ theme }) => theme.color.datePicker.hoverIconColor};
        ${commonTransition};
      };
    }
  };

  /************************* HOVER *************************/

  :hover {
    border: 1px solid ${({ theme }) => theme.color.datePicker.hoverBorderColor} !important;
    ${commonTransition};
  };

  /************************* FOCUS *************************/

  :focus-within {
    ${commonTransition};
    border: 1px solid ${({ theme }) => theme.color.datePicker.focusBorderColor} !important;

    svg {
      color: ${({ theme }) => theme.color.datePicker.suffixIconColor};
    };
  };

  /* Garantir que hover não sobrescreva o focus */
  :focus-within:hover {
    border: 1px solid ${({ theme }) => theme.color.datePicker.focusBorderColor} !important;
  };
`;

/** *********************** CALENDAR ************************ */

export const PanelContainer = styled.div<{ isLandscapeMobile: boolean }>`
  background-color: ${({ theme }) => theme.color.datePicker.calendarBackgroundColor};

  .ant-picker-body {
    display: flex;
    align-items: center;
    justify-content: center;
  };

  .ant-picker-header-view, .ant-picker-header-view button, .ant-picker-header button {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  };
  
  ${({ isLandscapeMobile }) => isLandscapeMobile && css`
    .ant-picker-body {
      padding: 0 8px !important;
    };

    th {
      height: 30px !important;
    };
  `};

  /************************* HEADER *************************/

  .ant-picker-header {
    border-bottom: 1px solid ${({ theme }) => theme.color.datePicker.headerBorderColor};
  };

  ${({ isLandscapeMobile }) => isLandscapeMobile && css`
    .ant-picker-header {
      padding: 0;
    };
  `};

  /************************* FOOTER *************************/

  .ant-picker-footer {
    display: none;
  };

  /************************* CELLS WITH DATES *************************/

  .ant-picker-cell-inner {
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-family: ${({ theme }) => theme.font.regular} !important;
    font-size: 12px;
    width: 100% !important;
  };

  ${({ isLandscapeMobile }) => isLandscapeMobile && css`
    .ant-picker-cell-inner {
      height: 25px !important;
    };
  `};

  /************************* DATES *************************/

  .ant-picker-dropdown, .ant-picker-cell {
    padding: 2px 0;
  };

  .ant-picker-dropdown, .ant-picker-cell::before {
    transition: all 0.3s ease;
  };

  .ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-range-hover::after,
  .ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-range-hover-end::after,
  .ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-range-hover-start::after {
    border: none !important;
  };

  .ant-picker-cell-disabled .ant-picker-cell-inner {
    background-color: ${({ theme }) => theme.color.datePicker.dateDisabledBackgroundColor};
    border-radius: 0 !important;
  };

  & .ant-picker-cell-range-hover-end .ant-picker-cell-inner,
  & .ant-picker-cell-range-hover-start .ant-picker-cell-inner {
    :not(:hover) {
      border-radius: 4px;
    };
  };

  /************************* HOVER *************************/

  .ant-picker-cell-in-view:not(
  .ant-picker-cell-range-end,
  .ant-picker-cell-range-start
  ):hover .ant-picker-cell-inner {
    background-color: ${({ theme }) => theme.color.datePicker.dateHoverBackgroundColor} !important;
    transition: all 0.3s ease;
    cursor: pointer;
    border-radius: 0%;
  };

  .ant-picker-cell-inner::after {
    inset-inline-start: 0 !important;
  };

  /************************* CELLS WITH START/END DATES *************************/

  .ant-picker-cell-in-view.ant-picker-cell-range-start,
  .ant-picker-cell-in-view.ant-picker-cell-range-end {
    > div {
      color: ${({ theme }) => theme.color.datePicker.selectedDateTextColor};
    };
  };

  /************************* DATES BETWEEN SELECTION *************************/

  .ant-picker-dropdown, .ant-picker-cell-in-view.ant-picker-cell-in-range::before {
    background-color: ${({ theme }) => theme.color.datePicker.rangeBackgroundColor} !important;
    height: 36px !important;
    width: 36px !important;
    transition: all 0.3s ease;
  };

  ${({ isLandscapeMobile }) => isLandscapeMobile && css`
    .ant-picker-dropdown, .ant-picker-cell-in-view.ant-picker-cell-in-range::before {
      height: 30px !important;
    };
  `};

  .ant-picker-cell-inner::after {
    height: 36px !important;
    width: 36px !important;
    transition: all 0.3s ease;
  };

  /************************* MONTH DAYS *************************/

  .ant-picker-cell-in-view {
    color: ${({ theme }) => theme.color.datePicker.daysTextColor} !important;
    transition: all 0.3s ease;
  };

  /************************* HEADER DETAILS *************************/

  .ant-picker-year-btn, .ant-picker-month-btn {
    font-family: ${({ theme }) => theme.font.regular} !important;
    font-size: 14px;
    pointer-events: none;
  };

  .ant-picker-header-next-btn,
  .ant-picker-header-super-next-btn,
  .ant-picker-header-prev-btn,
  .ant-picker-header-super-prev-btn {
    transition: all 0.3s ease !important;
    :hover {
      color: ${({ theme }) => theme.color.datePicker.nextMonthButtonIconColor} !important;
    };
  };

  ${({ isLandscapeMobile }) => isLandscapeMobile && css`
    .ant-picker-header, .ant-picker-header button {
      height: 30px !important;
    };
  `};

  /************************* WEEK DAYS *************************/

  .ant-picker-content {
    tr > th {
      font-family: ${({ theme }) => theme.font.regular} !important;
      font-size: 12px;
    };
  };
`;
